import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Paper,
  Link
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  Public as PublicIcon,
  Gavel as CourtIcon,
  Book as LawBookIcon,
  Language as ImmigrationIcon,
  Home as LandIcon,
  School as LawSocietyIcon,
  Article as BlogIcon,
  DirectionsCar as CarIcon,
  Business as BusinessIcon,
  Balance as DivorceIcon,
  LocalPolice as PoliceIcon,
  Security as PrivacyIcon,
  Fingerprint as TrademarkIcon,
} from "@mui/icons-material";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(10, 0, 6, 0),
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2),
  };
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));

const Breadcrumbs = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontSize: '0.9rem',
}));

const BreadcrumbLink = styled(RouterLink)(({ theme }) => ({
  color: 'white',
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.secondary.main,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  marginBottom: theme.spacing(3),
  color: "#0e3158",
  position: "relative",
  paddingBottom: theme.spacing(1),
  textAlign: "center",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "3px",
    backgroundColor: "#f0b94d",
  },
}));

const ResourceCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
}));

const ResourceTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.2rem",
  marginBottom: theme.spacing(1),
  color: "#0e3158",
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: theme.spacing(2),
  color: '#0e3158',
}));

const CtaSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: '#f5f7fa',
  borderRadius: '8px',
}));

// Data for external links
const externalLinks = [
  {
    title: "Alberta Courts",
    description: "Official website for Alberta's courts. Find information on courthouse locations, court rules, forms, and scheduling for the Court of King's Bench, Court of Appeal, and Provincial Court. This site is useful for understanding court procedures and accessing necessary legal forms.",
    url: "https://www.albertacourts.ca/",
    icon: <CourtIcon fontSize="large" />
  },
  {
    title: "CanLII (Canadian Legal Information Institute)",
    description: "A free online legal database of Canadian laws and cases. CanLII provides access to Alberta statutes and regulations, as well as court decisions from Alberta and across Canada. It's a great tool to research precedents or read the full text of laws relevant to your situation.",
    url: "https://www.canlii.org/",
    icon: <LawBookIcon fontSize="large" />
  },
  {
    title: "Immigration, Refugees and Citizenship Canada (IRCC)",
    description: "The official Government of Canada site for immigration and citizenship information. Here you can find guides and application forms for visas, permanent residence (including Express Entry and sponsorship), citizenship applications, and the latest updates on immigration programs and processing times.",
    url: "https://www.canada.ca/en/immigration-refugees-citizenship.html",
    icon: <ImmigrationIcon fontSize="large" />
  },
  {
    title: "Alberta Land Titles",
    description: "The Alberta government's Land Titles information page. It explains how to conduct land title searches, register property documents, and provides forms related to property transactions. This resource is valuable for anyone dealing with real estate in Alberta, offering insight into title transfer requirements and fees.",
    url: "https://www.alberta.ca/land-titles.aspx",
    icon: <LandIcon fontSize="large" />
  },
  {
    title: "Law Society of Alberta",
    description: "The regulatory body for Alberta's legal profession. Their website includes resources such as a lawyer directory, information on the complaint process, and public legal education materials. It's also a place to verify the credentials and good standing of any lawyer in Alberta.",
    url: "https://www.lawsociety.ab.ca/",
    icon: <LawSocietyIcon fontSize="large" />
  }
];


// Data for blog posts
const blogPosts = [
  {
    title: "5 Essential Steps to Take After a Car Accident",
    description: "When an accident happens, the actions you take in the immediate aftermath can significantly impact any injury claim you pursue. In this post, we walk through five critical steps to protect your health and your rights – from calling for help and gathering evidence at the scene, to notifying insurance and avoiding common pitfalls. Whether you're a new driver or a seasoned one, being prepared can make all the difference.",
    icon: <CarIcon fontSize="large" />,
    comingSoon: true
  },
  {
    title: "Divorce Mediation vs. Court Litigation: What's Right for You?",
    description: "Not every divorce has to play out in a courtroom battle. We compare mediation and litigation in the context of family law. This article explains how mediation works, its benefits (like privacy and reduced conflict), and when court might still be necessary (for example, in high-conflict or emergency situations). By understanding both options, you can choose the path that best fits your situation and could save time, stress, and money.",
    icon: <DivorceIcon fontSize="large" />,
    comingSoon: true
  },
  {
    title: "Starting a Business in Alberta: Legal Checklist for Entrepreneurs",
    description: "A handy checklist-style article for anyone looking to launch a small business. We cover the key legal considerations: choosing a business structure (sole proprietorship vs. incorporation), registering your business name, obtaining necessary licenses or permits, drafting contracts, understanding employment law if you'll have staff, and protecting your intellectual property. Think of it as a roadmap to help new business owners start on solid legal footing.",
    icon: <BusinessIcon fontSize="large" />,
    comingSoon: true
  },
  {
    title: "Understanding Your Rights at a Traffic Stop (Impaired Driving Law Update)",
    description: "Police procedures at roadside stops have evolved, especially with new impaired driving laws and tools like mandatory alcohol screening. This post breaks down your rights and obligations if you're pulled over in a DUI checkpoint or traffic stop. We discuss what you must do (provide identification, comply with certain tests) and what you don't have to do (answer incriminating questions), as well as recent changes in the law. Stay informed to protect your rights during these encounters.",
    icon: <PoliceIcon fontSize="large" />,
    comingSoon: true
  },
  {
    title: "The New Era of Canadian Privacy Law: What Businesses Need to Know",
    description: "With technology advancing and recent updates like the Digital Charter Implementation Act on the horizon, privacy compliance is more important than ever. In this piece, we outline proposed changes to federal privacy legislation (including potential new fines and individual privacy rights) and what Alberta businesses should do now to prepare. Topics include beefing up your privacy policy, data breach response, and ensuring you have consent for data collection. It's a must-read for any business handling customer data.",
    icon: <PrivacyIcon fontSize="large" />,
    comingSoon: true
  },
  {
    title: "Trademark 101: Protecting Your Brand in Canada",
    description: "A beginner's guide to trademarks: what they are, why they matter, and how to get one. We use plain language to explain the difference between ™ and ® symbols, the process of registering a trademark with examples, and how trademarks interact with domain names and business names. If you're a business owner or creative professional, this post will clarify how you can shield your brand identity from copycats and why early action is beneficial.",
    icon: <TrademarkIcon fontSize="large" />,
    comingSoon: true
  }
];

  function LegalResources() {
    return (
      <>
      <SeoMetaTags
        slug="legal-resources"
        defaultTitle="Legal Resources - Helpful Guides & Information | Sentry Law"
        defaultDescription="Access free legal resources, downloadable guides, and helpful external links for legal matters in Alberta. Empowering clients with knowledge and tools."
      />
      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">

          <PageTitle variant="h1">
            Legal Resources
          </PageTitle>
          <Typography variant="subtitle1" sx={{ maxWidth: 800, mx: 'auto', mb: 3, lineHeight: 1.7 }}>
            Helpful information, tools, and references to support your legal journey
          </Typography>
        </Container>
      </PageHeader>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        
        {/* External Links Section */}
        <SectionTitle variant="h2" gutterBottom>
          Helpful External Resources
        </SectionTitle>
        
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {externalLinks.map((link, index) => (
            <Grid item xs={12} md={6} key={index}>
              <ResourceCard>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconWrapper>
                      {link.icon}
                    </IconWrapper>
                    <ResourceTitle variant="h3">
                      {link.title}
                    </ResourceTitle>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {link.description}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<PublicIcon />}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      bgcolor: '#0e3158', 
                      '&:hover': { bgcolor: '#0a2548' }
                    }}
                  >
                    Visit Website
                  </Button>
                </CardActions>
              </ResourceCard>
            </Grid>
          ))}
        </Grid>
        
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
          These links provide general information and should not be taken as legal advice. For advice on how the law applies to your specific circumstances, please contact a qualified lawyer.
        </Typography>

        {/* CTA Section */}
        <CtaSection>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700, color: '#0e3158' }}>
            Need Personalized Legal Advice?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            While these resources provide helpful information, nothing replaces the value of personalized legal advice for your specific situation. Contact us today to schedule a free consultation.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact-us"
              sx={{ 
                bgcolor: '#f0b94d', 
                color: '#0e3158', 
                fontWeight: 600,
                '&:hover': { bgcolor: '#d9a843' }, 
                px: 3, 
                py: 1.5 
              }}
            >
              Schedule Free Consultation
            </Button>

          </Box>
        </CtaSection>
      </Container>
    </>
  );
}

export default LegalResources;